import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { VButton, VFlex, VInput, useFlag } from '@/package';
import { useOnboardingLayout } from '@/provider/layout';
import { useAuthStore } from '@/store';
import type { OnboardingForm } from '@/type';

const SignInPage = () => {
  const { setTitle } = useOnboardingLayout();
  const [searchParams, setSearchParams] = useSearchParams();
  const { signIn, signInByProvider, setChangePasswordInfo } = useAuthStore();
  const [errorMessage, setErrorMessage] = useState('');
  const code = searchParams.get('code');
  const {
    value: isLoading,
    setTrue: setIsLoading,
    setFalse: setIsNotLoading,
  } = useFlag(!!code);
  const navigate = useNavigate();
  const { watch, register, setValue } = useFormContext<OnboardingForm>();

  const onSignInButtonClick = () => {
    const email = watch('email');
    const password = watch('password');

    if (!email || !password) {
      setErrorMessage('이메일과 비밀번호를 입력하세요');
      return Promise.resolve();
    }

    return signInAction(signIn({ email, password }));
  };

  const signInAction = (action: Promise<void>) => {
    setIsLoading();

    return action
      .catch(err => {
        console.warn(err);

        searchParams.delete('code');

        setSearchParams(searchParams);

        const resultCode = err.cause.data.resultCode;

        if (resultCode === '2403') {
          const { marketingPromotion = false, consentAITraining = false } =
            err.cause.data?.resultData ?? {};

          setChangePasswordInfo({
            email: watch('email'),
            password: watch('password'),
            marketingPromotion,
            consentAITraining,
          });

          navigate('/auth/new-password');
          return;
        }

        setErrorMessage(err.message);
      })
      .finally(() => {
        setIsNotLoading();
      });
  };

  useEffect(() => {
    if (!code) return;

    signInAction(signInByProvider(code));
  }, [searchParams]);

  useEffect(() => {
    const email = searchParams.get('email');

    if (!email) return;

    setValue('email', email);
  }, [searchParams]);

  useEffect(() => {
    setTitle('Sign in to your account');
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setErrorMessage('');
  }, [watch('email'), watch('password')]);

  return (
    <VFlex
      direction="col"
      gap={1}
      justify="center"
      items="center"
      className="w-[296px] pt-6"
    >
      <div className="hidden h-px w-[296px] bg-gray-300" />

      <VFlex direction="col" className="w-full" gap={0.5}>
        <VInput
          {...register('email')}
          type="email"
          label="email"
          valid={!errorMessage.length}
          fluid
          required
        />
        <VInput
          {...register('password')}
          type="password"
          label="password"
          comment={errorMessage}
          valid={!errorMessage.length}
          fluid
          required
          onKeyUp={e => e.key === 'Enter' && onSignInButtonClick()}
        />
      </VFlex>

      <VFlex direction="col" className="w-full" gap={1.25}>
        <VButton
          disabled={!watch('email') || !watch('password')}
          loading={isLoading}
          fluid
          size="lg"
          onClick={onSignInButtonClick}
        >
          Log in
        </VButton>

        <VFlex direction="col" className="w-full" gap={0.25} items="center">
          <div
            role="button"
            className="text-body-lg font-bold"
            onClick={() => navigate('/auth/reset-password')}
          >
            Reset password
          </div>

          <VFlex
            className="w-full text-body-lg"
            justify="center"
            items="center"
            gap={1}
          >
            <span>Don't have an account?</span>
            <Link to="/auth/signup" className="font-bold">
              Create account
            </Link>
          </VFlex>
        </VFlex>
      </VFlex>
    </VFlex>
  );
};

export default SignInPage;
